import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import FlexLib from '@flex/lib';
import productsService from '../../services/productsService';
import { Grid, Stack, Container, Button } from '@mui/material';
import { FlexAlerts, AlertSeverityTypes } from '../../components/Message/Alerts/Alerts';
import { Topheader } from '../../components/Topheader';
import TableV2, { spreadPaginationOptions } from '../../components/Table/TableV2';
import { ActivateButton, EditButton } from '../../components/Table/components';
const { formatCurrency } = FlexLib.utils.feeService;

const ProductsTable = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isUserAllowedToExport, setIsUserAllowedToExport] = useState(false);
  const [isUserAllowedToChangeStatus, setIsUserAllowedToChangeStatus] = useState(false);
  const [isUserAllowedToEditProduct, setIsUserAllowedToEditProduct] = useState(false);
  const [isUserAllowedToAddProduct, setIsUserAllowedToAddProduct] = useState(false);
  const [messages, setMessages] = useState([]);
  const [products, setProducts] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationOptions, setPaginationOptions] = useState({});

  useEffect(() => {
    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setIsUserAllowedToExport(user?.role <= 1 || !!user?.permissions?.exportRecord);
    setIsUserAllowedToChangeStatus(user?.role <= 3);
    setIsUserAllowedToEditProduct(user?.role <= 3);
    setIsUserAllowedToAddProduct(user?.role <= 4);
  }, []);

  const getProducts = (paginationOptions) => {
    const query = spreadPaginationOptions(paginationOptions);

    setIsLoading(true);
    productsService
      .getProductsList(query)
      .then((response) => {
        if (response.data) {
          setProducts(response.data?.products);
          setRowCount(response.data?.rowCount);
        }
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not retrieve products. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchProducts = (newPaginationOptions) => {
    setPaginationOptions(newPaginationOptions);
    getProducts(newPaginationOptions);
  };

  const handleToggleProductStatus = (productId, newStatus) => {
    setIsLoading(true);
    productsService
      .updateProductStatus(productId, newStatus)
      .then(() => {
        getProducts(paginationOptions);
      })
      .catch((error) => {
        setMessages([
          {
            message: `Could not update product status. ${error?.response?.data?.message ?? ''}`,
            severity: AlertSeverityTypes.error,
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditProduct = (id) => {
    history.push('/EditProducts/' + id);
  };

  const handleAddProduct = () => {
    history.push('/AddProducts');
  };

  const columns = [
    { field: 'name', type: 'string', headerName: 'Name', minWidth: 200, flex: 1 },
    { field: 'description', type: 'string', headerName: 'Description', minWidth: 250, flex: 2 },
    {
      field: 'amount',
      type: 'number',
      headerName: 'Amount',
      minWidth: 150,
      flex: 1,
      valueFormatter: (value) => formatCurrency(value),
    },
    { field: 'notes', type: 'string', headerName: 'Notes', minWidth: 200, flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={2}>
          {isUserAllowedToEditProduct && (
            <EditButton
              id={`edit-${params.row._id}`}
              dataCy={`edit-product-${params.row.name}`}
              onClick={() => handleEditProduct(params.row._id)}
            />
          )}
          {isUserAllowedToChangeStatus && (
            <ActivateButton
              id={`activate-${params.row._id}`}
              onClick={() => handleToggleProductStatus(params.row._id, !params.row.status)}
              isActive={params.row.status}
            />
          )}
        </Stack>
      ),
    },
  ];

  return (
    <div className="rightPanel">
      <Topheader />
      <div className="contentPart">
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginBottom: '40px' }}
          className="terminal-container term-add"
        >
          <Grid container rowSpacing={2}>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <h3 className="terminal-title">Products</h3>
            </Grid>
            <Grid item xs={12}>
              <FlexAlerts messages={messages} />
            </Grid>
            <Grid item xs={12}>
              <TableV2
                rows={products}
                columns={columns}
                fetchData={fetchProducts}
                rowCount={rowCount}
                isLoading={isLoading}
                isExportable={isUserAllowedToExport}
                searchConfig={{ isSearchBar: true }}
                toolbarExtension={
                  <>
                    {isUserAllowedToAddProduct && (
                      <Button id="add-product-button" data-cy="add-product-button" onClick={handleAddProduct}>
                        Add Product
                      </Button>
                    )}
                  </>
                }
              />
            </Grid>
          </Grid>
        </Container>
      </div>
    </div>
  );
};

export default ProductsTable;
